/**
 * @fileoverview Property Search Hook and Provider
 * 
 * Persistence: Filters and results are saved to localStorage for:
 * - Page refresh recovery
 * - Session continuity
 */

import { createContext, useContext, useReducer, useCallback } from 'react';
import { PropertyActionTypes, initialState } from './propertySearchTypes.js';
import { propertyReducer } from './propertySearchReducer.js';
import { saveFilters, loadFilters, saveResults, loadResults } from './aiSearchStorage.js';
import { enrichProperties } from '../services/propertyEnrichmentService.js';

/**
 * Get initial state with persisted data from localStorage
 * This runs synchronously before first render to ensure UI consistency
 * Note: Called by useReducer's lazy initializer - arg is ignored
 */
function getInitialState(_ignored) {
  try {
    const savedFilters = loadFilters();
    const savedResults = loadResults();
    
    console.log('[usePropertySearch] getInitialState called:', { 
      savedFilters, 
      savedResultsCount: savedResults?.length,
      savedResultsPreview: savedResults?.slice(0, 2)
    });

    if (savedResults && savedResults.length > 0) {
      console.log('[usePropertySearch] Restoring', savedResults.length, 'results from localStorage');
      return {
        ...initialState,
        filters: savedFilters && Object.keys(savedFilters).length > 0 
          ? { ...initialState.filters, ...savedFilters }
          : initialState.filters,
        results: savedResults,
        pagination: {
          ...initialState.pagination, 
          total: savedResults.length, 
          totalPages: Math.ceil(savedResults.length / 12)
        },
      };
    }

    console.log('[usePropertySearch] No saved results, using initial state');
    return initialState;
  } catch (error) {
    console.error('[usePropertySearch] Error loading from storage:', error);
    return initialState;
  }
}

/**
 * Properties API URL configuration
 * - Production: AWS Lambda via API Gateway (Socrata-backed)
 * - Development: Vite proxy to local Express backend (Socrata-backed)
 * 
 * Both use CT Open Data Portal (211K+ properties) via Socrata API
 */
/* global __AWS_API_URL__ */
const AWS_PROPERTIES_API = `${typeof __AWS_API_URL__ !== 'undefined' ? __AWS_API_URL__ : ''}/properties`;
const LOCAL_PROPERTIES_API = '/api/socrata/properties';

function getPropertiesApiUrl() {
  const isProduction = import.meta.env.PROD;
  return isProduction ? AWS_PROPERTIES_API : LOCAL_PROPERTIES_API;
}

// Context
const PropertyContext = createContext();

// Provider component
export function PropertyProvider({ children }) {
  // Initialize state synchronously from localStorage
  // This ensures persisted results are available on first render (toggle visible immediately)
  const [state, dispatch] = useReducer(propertyReducer, null, getInitialState);

  const searchProperties = useCallback(async (filters = {}, page = 1) => {
    console.log('[usePropertySearch] searchProperties called with:', filters);
    dispatch({ type: PropertyActionTypes.SET_LOADING, payload: true });

    try {
      // Clean filters - remove null/undefined values
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v != null && v !== '')
      );
      console.log('[usePropertySearch] Clean filters:', cleanFilters);

      const queryParams = new URLSearchParams({
        ...cleanFilters,
        page: page.toString(),
        limit: '12',
      });

      const apiUrl = getPropertiesApiUrl();
      console.log('[usePropertySearch] Fetching from:', `${apiUrl}?${queryParams}`);
      const response = await fetch(`${apiUrl}?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[usePropertySearch] Response data:', data);
      
      // Extract properties from response
      const rawProperties = data.data || data.properties || [];
      console.log('[usePropertySearch] Raw properties count:', rawProperties.length);
      
      // Enrich properties with CAMA data (beds, baths, sqft, etc.)
      const enrichedProperties = await enrichProperties(rawProperties);
      console.log('[usePropertySearch] Enriched properties count:', enrichedProperties.length);
      
      // Update data object with enriched properties
      const enrichedData = {
        ...data,
        data: data.data ? enrichedProperties : undefined,
        properties: data.properties ? enrichedProperties : undefined,
      };
      
      dispatch({ type: PropertyActionTypes.SET_RESULTS, payload: enrichedData });
      
      // Save results to localStorage for page refresh recovery
      // Always save (even empty array) to keep localStorage in sync with current state
      console.log('[usePropertySearch] Saving to localStorage:', enrichedProperties.length, 'results');
      saveResults(enrichedProperties);
      saveFilters(cleanFilters);
      
      // Verify save worked
      const verified = loadResults();
      console.log('[usePropertySearch] Verified localStorage has:', verified?.length, 'results');
    } catch (error) {
      console.error('[usePropertySearch] Error:', error);
      dispatch({ type: PropertyActionTypes.SET_ERROR, payload: error.message });
    }
  }, []);

  const setFilters = useCallback((filters) => {
    dispatch({ type: PropertyActionTypes.SET_FILTERS, payload: filters });
    // Save filters to localStorage for page refresh recovery
    saveFilters(filters);
  }, []);

  /**
   * Load more properties for infinite scroll
   * Appends results to existing results instead of replacing
   */
  const loadMoreProperties = useCallback(async () => {
    const { filters, pagination, loading } = state;
    
    // Don't fetch if already loading or no more pages
    if (loading || pagination.page >= pagination.totalPages) {
      console.log('[usePropertySearch] loadMoreProperties: skipped (loading or no more pages)');
      return;
    }

    const nextPage = pagination.page + 1;
    console.log('[usePropertySearch] loadMoreProperties: fetching page', nextPage);
    dispatch({ type: PropertyActionTypes.SET_LOADING, payload: true });

    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v != null && v !== '')
      );

      const queryParams = new URLSearchParams({
        ...cleanFilters,
        page: nextPage.toString(),
        limit: '12',
      });

      const apiUrl = getPropertiesApiUrl();
      const response = await fetch(`${apiUrl}?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Load more failed: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[usePropertySearch] loadMoreProperties response:', data);
      dispatch({ type: PropertyActionTypes.APPEND_RESULTS, payload: data });
      
      // Update localStorage with all results (existing + new)
      const newResults = data.data || data.properties || [];
      const allResults = [...state.results, ...newResults];
      saveResults(allResults);
    } catch (error) {
      console.error('[usePropertySearch] loadMoreProperties error:', error);
      dispatch({ type: PropertyActionTypes.SET_ERROR, payload: error.message });
    }
  }, [state]);

  const setPage = useCallback((page) => {
    dispatch({ type: PropertyActionTypes.SET_PAGE, payload: page });
  }, []);

  const clearResults = useCallback(() => {
    dispatch({ type: PropertyActionTypes.CLEAR_RESULTS });
    // Clear saved results from localStorage
    saveResults([]);
    saveFilters({});
  }, []);

  const value = {
    ...state,
    searchProperties,
    loadMoreProperties,
    setFilters,
    setPage,
    clearResults,
  };

  return (
    <PropertyContext.Provider value={value}>
      {children}
    </PropertyContext.Provider>
  );
}

// Hook
export function usePropertySearch() {
  const context = useContext(PropertyContext);
  if (!context) {
    throw new Error('usePropertySearch must be used within a PropertyProvider');
  }
  return context;
}